import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Wallet, ArrowUpRight, ArrowDownRight, Clock, CheckCircle, XCircle, DollarSign, AlertCircle } from 'lucide-react';

import { API } from '../utils/apiConfig';

export const DriverWallet = () => {
  const { t } = useTranslation();
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [bankDetails, setBankDetails] = useState('');
  const [requesting, setRequesting] = useState(false);
  const [showPayoutForm, setShowPayoutForm] = useState(false);
  const [msg, setMsg] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const loadWallet = async () => {
    try {
      const { data } = await axios.get(`${API}/wallet/balance`, { headers });
      setWallet(data);
    } catch (e) {
      console.error('Wallet load failed:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadWallet(); }, []);

  const handleRequestPayout = async (e) => {
    e.preventDefault();
    const amount = parseFloat(payoutAmount);
    if (!amount || amount < 5) {
      setMsg({ type: 'error', text: t('wallet.minPayout') });
      return;
    }
    if (amount > wallet.available_balance) {
      setMsg({ type: 'error', text: t('wallet.insufficientBalance') });
      return;
    }
    setRequesting(true);
    try {
      await axios.post(`${API}/wallet/request-payout`, { amount, bank_details: bankDetails }, { headers });
      setMsg({ type: 'success', text: t('wallet.payoutRequested') });
      setShowPayoutForm(false);
      setPayoutAmount('');
      loadWallet();
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.detail || t('wallet.payoutError') });
    } finally {
      setRequesting(false);
    }
    setTimeout(() => setMsg(null), 4000);
  };

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>{t('common.loading')}</div>;
  if (!wallet) return null;

  const statusIcon = (s) => {
    if (s === 'sent') return <CheckCircle size={14} style={{ color: '#059669' }} />;
    if (s === 'rejected') return <XCircle size={14} style={{ color: '#dc2626' }} />;
    return <Clock size={14} style={{ color: '#d97706' }} />;
  };

  const fee = wallet.service_fee_percent;

  return (
    <div data-testid="driver-wallet-section" style={{ marginBottom: '2rem' }}>
      <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1.2rem', fontWeight: 700, color: '#1f2937', marginBottom: '1rem' }}>
        <Wallet size={22} /> {t('wallet.title')}
      </h2>

      {/* Balance Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem', marginBottom: '1.25rem' }}>
        <div data-testid="wallet-available-balance" style={{ background: 'linear-gradient(135deg, #1d4ed8, #3b82f6)', borderRadius: '12px', padding: '1.25rem', color: 'white' }}>
          <div style={{ fontSize: '0.75rem', opacity: 0.8 }}>{t('wallet.available')}</div>
          <div style={{ fontSize: '1.75rem', fontWeight: 800 }}>{Number(wallet.available_balance ?? 0).toFixed(2)}€</div>
        </div>
        <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '1.25rem', border: '1px solid #e2e8f0' }}>
          <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{t('wallet.pending')}</div>
          <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#d97706' }}>{Number(wallet.pending_balance ?? 0).toFixed(2)}€</div>
        </div>
        <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '1.25rem', border: '1px solid #e2e8f0' }}>
          <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{t('wallet.totalEarned')}</div>
          <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#059669' }}>{Number(wallet.total_earned ?? 0).toFixed(2)}€</div>
        </div>
        <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '1.25rem', border: '1px solid #e2e8f0' }}>
          <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{t('wallet.withdrawn')}</div>
          <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#1f2937' }}>{Number(wallet.total_withdrawn ?? 0).toFixed(2)}€</div>
        </div>
      </div>

      {/* Payout Button / Form */}
      {!showPayoutForm ? (
        <button
          data-testid="request-payout-btn"
          onClick={() => setShowPayoutForm(true)}
          disabled={wallet.available_balance < 5}
          style={{
            width: '100%', padding: '0.85rem', borderRadius: '10px', border: 'none',
            background: wallet.available_balance >= 5 ? 'linear-gradient(135deg, #059669, #10b981)' : '#e5e7eb',
            color: wallet.available_balance >= 5 ? 'white' : '#9ca3af',
            fontWeight: 600, fontSize: '0.95rem', cursor: wallet.available_balance >= 5 ? 'pointer' : 'not-allowed',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem'
          }}
        >
          <ArrowUpRight size={18} /> {t('wallet.requestPayout')}
        </button>
      ) : (
        <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '1.25rem', border: '1px solid #e2e8f0' }}>
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={{ fontSize: '0.85rem', color: '#374151', fontWeight: 600 }}>{t('wallet.payoutAmount')}</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.25rem' }}>
              <input
                type="number" value={payoutAmount} onChange={(e) => setPayoutAmount(e.target.value)}
                min="5" max={wallet.available_balance} step="0.01" required
                data-testid="payout-amount-input"
                style={{ flex: 1, padding: '0.6rem', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '1rem' }}
              />
              <span style={{ fontWeight: 600, color: '#374151' }}>€</span>
            </div>
            <small style={{ color: '#6b7280', fontSize: '0.75rem' }}>
              {t('wallet.feeInfo', { percent: fee })} {payoutAmount && parseFloat(payoutAmount) > 0 && (
                <> — {t('wallet.youReceive')}: <strong>{(parseFloat(payoutAmount) * (1 - (fee || 0) / 100)).toFixed(2)}€</strong></>
              )}
            </small>
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ fontSize: '0.85rem', color: '#374151', fontWeight: 600 }}>{t('wallet.bankDetails')} ({t('common.optional')})</label>
            <input
              type="text" value={bankDetails} onChange={(e) => setBankDetails(e.target.value)}
              placeholder="IBAN: DE89 3704 0044..."
              data-testid="bank-details-input"
              style={{ width: '100%', padding: '0.6rem', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '0.9rem', marginTop: '0.25rem' }}
            />
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button type="button" onClick={() => setShowPayoutForm(false)} style={{ flex: 1, padding: '0.65rem', borderRadius: '8px', border: '1px solid #d1d5db', background: 'white', cursor: 'pointer', fontWeight: 600 }}>
              {t('common.cancel')}
            </button>
            <button type="button" disabled={requesting} data-testid="confirm-payout-btn" onClick={handleRequestPayout} style={{
              flex: 1, padding: '0.65rem', borderRadius: '8px', border: 'none',
              background: 'linear-gradient(135deg, #059669, #10b981)', color: 'white', fontWeight: 600, cursor: 'pointer'
            }}>
              {requesting ? '...' : t('wallet.confirmPayout')}
            </button>
          </div>
        </div>
      )}

      {msg && (
        <div style={{ marginTop: '0.75rem', padding: '0.75rem', borderRadius: '8px', background: msg.type === 'success' ? '#f0fdf4' : '#fef2f2', color: msg.type === 'success' ? '#166534' : '#991b1b', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <AlertCircle size={16} /> {msg.text}
        </div>
      )}

      {/* Recent Payouts */}
      {wallet.recent_payouts.length > 0 && (
        <div style={{ marginTop: '1.25rem' }}>
          <h3 style={{ fontSize: '0.95rem', fontWeight: 600, color: '#374151', marginBottom: '0.75rem' }}>{t('wallet.recentPayouts')}</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {wallet.recent_payouts.map((p) => (
              <div key={p.id} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '0.75rem', borderRadius: '8px', background: '#f8fafc', border: '1px solid #e2e8f0'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  {statusIcon(p.status)}
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#1f2937' }}>{Number(p.net_amount ?? 0).toFixed(2)}€</div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                      {p.requested_at ? new Date(p.requested_at).toLocaleDateString() : ''} · {Number(p.fee ?? 0).toFixed(2)}€ {t('wallet.fee')}
                    </div>
                  </div>
                </div>
                <span style={{
                  padding: '0.2rem 0.6rem', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 600,
                  background: p.status === 'sent' ? '#dcfce7' : p.status === 'rejected' ? '#fef2f2' : '#fef3c7',
                  color: p.status === 'sent' ? '#166534' : p.status === 'rejected' ? '#991b1b' : '#92400e'
                }}>
                  {t(`wallet.status.${p.status}`)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
